import React from "react";
import { useObserver } from "mobx-react";
import { action, computed, observable } from "mobx";
import { Paper } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { ProjectFragment, ProjectStatus, ProjectVisibilityContext } from "@graphql";
import { AddRecipientChip, AvatarChip, Body } from "@components";
import { ProjectCard } from "@containers/ProjectCard";
import {
  ProjectSelect,
  ProjectSelectProps
} from "@containers/ProjectSelect/ProjectSelect";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import { useRegularProjects } from "@hooks";
import { store } from "@store";

type Props = {
  clearIcon?: React.ReactNode;
  clearable?: boolean;
  error?: boolean;
  value: string; // project id
  onChange: (projectId: string | null, project: ProjectFragment | null) => void;
  onSelectorClose?: () => void;
  store: ProjectSelectorStore;
  ProjectSelectProps?: Partial<ProjectSelectProps>;
  costCenter?: string; // constCenter.Name
  visibilityContext?: ProjectVisibilityContext;
};

type ProjectUser = {
  _id: string;
  Name: string;
  AvatarPic?: string;
};

export class ProjectSelectorStore {
  @observable filterUsers: ProjectUser[] = [];
  @observable projects: ProjectFragment[] = []; // <-- source of truth, set by component
  tags: Array<{ userId: string; tag: string }> = [];

  constructor(private setProject: (projectId: string) => void) {}

  @computed get matchingProjects() {
    const ids = this.filterUsers.map((x) => x._id);

    const activeProjects = this.projects.filter(
      (x) => x.Status !== ProjectStatus.COMPLETED
    );

    if (ids.length === 0) return activeProjects;

    return activeProjects
      .filter((project) => ids.all((x) => getProjectUserIds(project).includes(x)))
      .sortBy(
        (project) => -getProjectUserIds(project).filter((y) => ids.includes(y)).length
      );
  }

  @action setProjects = (projects: ProjectFragment[]) => {
    this.projects = projects ?? [];
    this.maybeSuggest();
  };

  @action addUser = (user: ProjectUser, tag?: string) => {
    this.filterUsers = this.filterUsers.concat(user).uniqueBy((x) => x._id);
    tag && this.tags.push({ userId: user._id, tag });
    this.maybeSuggest();
  };

  @action removeUser = (userId: string) => {
    this.filterUsers = this.filterUsers.filter((x) => x._id !== userId);
    this.maybeSuggest();
  };

  @action removeByTag = (tag: string) => {
    this.tags
      .filter((x) => x.tag === tag)
      .map((x) => x.userId)
      .forEach(this.removeUser);
  };

  @action reset = () => {
    this.filterUsers = [];
    this.tags = [];
  };

  @action handleSelectUser = () => {
    store()
      .ui.selectUser({ includeActiveUser: true })
      .then((user) => {
        this.addUser(user);
      });
  };

  maybeSuggest = () => {
    if (this.matchingProjects.length === 1) {
      this.setProject(this.matchingProjects[0]._id);
    }
  };

  projectById = (id: string) => this.projects.find((x) => x._id === id);
}

export function ProjectSelector(props: Props) {
  const { store: localStore, value, costCenter } = props;
  const classes = useStyles({});

  // Load projects from API (network-only) and push them into the MobX store
  const { regularProjects, loading } = useRegularProjects(false, props.visibilityContext);

  React.useEffect(() => {
    localStore.setProjects(regularProjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularProjects]);

  const project = value ? localStore.projectById(value) : null;
  const disabled = props.ProjectSelectProps?.disabled || loading;

  return useObserver(() => {
    if (project) {
      return (
        <ProjectCard
          data={project}
          onClear={() => props.onChange(null, null)}
          withClear={!disabled && props.clearable}
          clearIcon={props.clearIcon}
          costCenter={costCenter ?? ""}
        />
      );
    }

    return (
      <Paper style={{ padding: 16 }}>
        {!props.ProjectSelectProps?.disabled && (
          <div className={classes.userList}>
            <Body inline>{locale.ListProjectsWithMembers}</Body>
            {localStore.filterUsers.map((user) => (
              <AvatarChip
                key={user._id}
                size="normal"
                name={user.Name}
                src={user.AvatarPic}
                ChipProps={{
                  onDelete: () => localStore.removeUser(user._id),
                  label: <strong>{user.Name}</strong>
                }}
              />
            ))}

            <AddRecipientChip
              onClick={localStore.handleSelectUser}
              label={locale.Member}
            />
          </div>
        )}

        <ProjectSelect
          options={localStore.matchingProjects}
          error={props.error}
          fullWidth
          value={value}
          onChange={(v) => props.onChange(v, localStore.projectById(v))}
          onClose={props.onSelectorClose}
          {...props.ProjectSelectProps}
          disabled={disabled}
        />
      </Paper>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  userList: {
    ...theme.mixins.childrenMarginRight(4)
  }
}));

function getProjectUserIds(project: ProjectFragment) {
  return [
    project.Owner ? project.Owner.PersonId : null,
    project.Admin ? project.Admin.PersonId : null,
    ...(project.Members || []).map((x) => x.PersonId)
  ].filter((x) => x);
}
